// Where the practice dock sits. It starts at the bottom centre of the window;
// subscribers can drag it by the handle at the start of its bar to wherever
// suits them, and this browser remembers the spot (screens differ, so it
// isn't saved on the profile).
//
// The spot is how far across and down the window the bar is, as fractions of
// the room it has to move in: 0 is against the left (top) edge and 1 against
// the right (bottom) one. That keeps it on screen, and in the same corner,
// when the window is resized or the phone turned. The dock's styles do the
// arithmetic from --practice-dock-x and --practice-dock-y (see
// practice-dock.scss).

import React, { useEffect, useRef, useState } from 'react';
import { trackOnce } from '../../usage';
import { loadStored, saveStored } from './storage';

const STORAGE_KEY = 'gp-practice-dock-position';
// Gap between the dock and the window's edges ($practice-dock-edge).
const EDGE = 10;
// How far the pointer moves before pressing the handle becomes a drag.
const DRAG_FROM = 4;
// Arrow keys move it this far, with Shift held further.
const NUDGE = 10;
const BIG_NUDGE = 60;
// Dropped this close to where it starts out, it goes back there.
const SNAP_HOME = 24;
const DOUBLE_CLICK_MS = 400;

const clamp01 = value => Math.min(1, Math.max(0, value));

function storedPosition() {
    const stored = loadStored(STORAGE_KEY);
    return stored && Number.isFinite(stored.x) && Number.isFinite(stored.y)
        ? { x: clamp01(stored.x), y: clamp01(stored.y) }
        : null;
}

// The site's navigation stays over the top of the page, so the dock keeps
// below it (it would be hidden under it).
function navigationHeight() {
    const nav = document.querySelector('nav.navbar');
    return nav && window.getComputedStyle(nav).position === 'fixed' ? nav.offsetHeight : 0;
}

// The room the bar has to move in, and where it is now.
function measure(dock, bar) {
    const style = window.getComputedStyle(dock);
    const rect = bar.getBoundingClientRect();
    const top = parseFloat(style.getPropertyValue('--practice-dock-top')) || EDGE;
    const bottom = parseFloat(style.getPropertyValue('--practice-dock-bottom')) || EDGE;
    return {
        rect,
        top,
        width: document.documentElement.clientWidth - 2 * EDGE - rect.width,
        height: window.innerHeight - top - bottom - rect.height,
    };
}

// The spot that puts the bar's top left corner at (left, top).
function positionAt(room, left, top) {
    return {
        x: room.width > 0 ? clamp01((left - EDGE) / room.width) : 0.5,
        y: room.height > 0 ? clamp01((top - room.top) / room.height) : 1,
    };
}

// dockRef is the dock, which shows the bar the returned barRef is put on.
// Without a subscription the handle opens the paywall instead of moving it.
export default function useDockPosition(dockRef, user, onRequireSubscription) {
    const [stored, setStored] = useState(storedPosition);
    const [dragging, setDragging] = useState(false);
    const [layout, setLayout] = useState({ top: EDGE, barHeight: null });
    const barRef = useRef(null);
    const drag = useRef(null);
    const ignoreClicksUntil = useRef(0);
    const lastClick = useRef(0);

    const canMove = !!user.hasSubscription;
    // A signed-in user's subscription is known once their details arrive.
    // Until then keep the spot, so a subscriber's dock doesn't jump there.
    const waiting = !!user.isAuthenticated && user.has_subscription === undefined;
    const position = canMove || waiting ? stored : null;
    // Against the bottom it's still in the way of the end of the page, so
    // the page keeps its room for it.
    const floating = !!position && position.y < 1;

    useEffect(() => {
        const bar = barRef.current;
        const update = () => setLayout({ top: navigationHeight() + EDGE, barHeight: bar.offsetHeight });
        update();
        window.addEventListener('resize', update);
        const observer = window.ResizeObserver ? new window.ResizeObserver(update) : null;
        if (observer) {
            // Dark mode gives it a border.
            observer.observe(bar, { box: 'border-box' });
        }
        return () => {
            window.removeEventListener('resize', update);
            if (observer) {
                observer.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        document.body.classList.toggle('practice-dock-floating', floating);
        return () => document.body.classList.remove('practice-dock-floating');
    }, [floating]);

    const place = (next) => {
        setStored(next);
        saveStored(STORAGE_KEY, next);
        trackOnce('dock_moved');
    };

    const reset = () => place(null);

    const onPointerDown = (e) => {
        if (e.button !== 0 || drag.current) {
            return;
        }
        e.currentTarget.setPointerCapture(e.pointerId);
        const room = measure(dockRef.current, barRef.current);
        drag.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, room, to: null };
    };

    const onPointerMove = (e) => {
        const d = drag.current;
        if (!d || d.pointerId !== e.pointerId) {
            return;
        }
        const dx = e.clientX - d.x;
        const dy = e.clientY - d.y;
        if (!d.to) {
            if (Math.hypot(dx, dy) < DRAG_FROM) {
                return;
            }
            if (!canMove) {
                drag.current = null;
                ignoreClicksUntil.current = performance.now() + DOUBLE_CLICK_MS;
                onRequireSubscription();
                return;
            }
            setDragging(true);
        }
        d.to = positionAt(d.room, d.room.rect.left + dx, d.room.rect.top + dy);
        setStored(d.to);
    };

    const endDrag = (e) => {
        const d = drag.current;
        if (!d || d.pointerId !== e.pointerId) {
            return;
        }
        drag.current = null;
        if (!d.to) {
            return;
        }
        setDragging(false);
        // A drag ends in a click on the handle, which isn't one.
        ignoreClicksUntil.current = performance.now() + DOUBLE_CLICK_MS;
        const fromHome = Math.hypot((d.to.x - 0.5) * d.room.width, (d.to.y - 1) * d.room.height);
        place(fromHome < SNAP_HOME ? null : d.to);
    };

    // A double click (or tap) puts it back.
    const onClick = () => {
        const now = performance.now();
        if (now < ignoreClicksUntil.current) {
            return;
        }
        if (!canMove) {
            onRequireSubscription();
            return;
        }
        if (now - lastClick.current < DOUBLE_CLICK_MS) {
            lastClick.current = 0;
            reset();
            return;
        }
        lastClick.current = now;
    };

    const onKeyDown = (e) => {
        const step = e.shiftKey ? BIG_NUDGE : NUDGE;
        const move = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
        if (!move) {
            return;
        }
        e.preventDefault();
        if (!canMove) {
            onRequireSubscription();
            return;
        }
        const room = measure(dockRef.current, barRef.current);
        place(positionAt(room, room.rect.left + move[0], room.rect.top + move[1]));
    };

    const style = {
        '--practice-dock-top': `${layout.top}px`,
    };
    if (layout.barHeight) {
        style['--practice-dock-bar-height'] = `${layout.barHeight}px`;
    }
    if (position) {
        style['--practice-dock-x'] = position.x;
        style['--practice-dock-y'] = position.y;
    }

    return {
        barRef,
        style,
        className: {
            'practice-dock--moved': !!position,
            // In the top half the panel opens below the bar.
            'practice-dock--below': !!position && position.y < 0.5,
            'practice-dock--dragging': dragging,
        },
        handleProps: {
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onLostPointerCapture: endDrag,
            onClick,
            onKeyDown,
        },
    };
}

// The grip at the start of the dock's bar.
export function DockHandle({ dock }) {
    return (
        <button
            type="button"
            className="practice-dock-handle"
            aria-label={gettext('Move the practice tools')}
            title={gettext('Drag to move. Double-click to put it back.')}
            {...dock.handleProps}
        >
            <svg width="8" height="16" viewBox="0 0 8 16" aria-hidden="true">
                {[2, 8, 14].map(y => [2, 6].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" fill="currentColor" />))}
            </svg>
        </button>
    );
}
